<script setup lang="ts">
const route = useRoute()
const siteUrl = 'https://re-design.de'
const articleContentRef = ref<HTMLElement | null>(null)
const copyFeedback = ref('')

const { data: article } = await useAsyncData(`article-${route.path}`, () =>
  queryCollection('articles').path(route.path).first()
)

if (!article.value) {
  await navigateTo('/articles')
}

// Prev / Next
const { data: allArticles } = await useAsyncData('articles-nav', () =>
  queryCollection('articles')
    .where('draft', '=', false)
    .order('date', 'DESC')
    .all()
)

const currentIndex = computed(() =>
  allArticles.value?.findIndex(a => a.path === route.path) ?? -1
)
const prevArticle = computed(() =>
  currentIndex.value > 0 ? allArticles.value![currentIndex.value - 1] : null
)
const nextArticle = computed(() =>
  currentIndex.value < (allArticles.value?.length ?? 0) - 1
    ? allArticles.value![currentIndex.value + 1]
    : null
)

function readingTime(body: unknown): number {
  const text = JSON.stringify(body)
  const wordCount = text.split(/\s+/).length
  return Math.ceil(wordCount / 200)
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const canonicalUrl = computed(() => `${siteUrl}${route.path}`)

const coverImageUrl = computed(() => {
  const cover = article.value?.cover
  if (!cover) return `${siteUrl}/images/og-image.jpg`
  if (/^https?:\/\//i.test(cover)) return cover
  return `${siteUrl}${cover.startsWith('/') ? cover : `/${cover}`}`
})

function clearCopyFeedbackSoon() {
  window.setTimeout(() => {
    copyFeedback.value = ''
  }, 1800)
}

function buildSectionUrl(sectionId: string) {
  return `${window.location.origin}${route.path}#${sectionId}`
}

function createCopyButton(sectionId: string, sectionTitle: string) {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'heading-copy-link-btn'
  button.title = 'Link zu diesem Abschnitt kopieren'
  button.setAttribute('aria-label', `Link zu Abschnitt kopieren: ${sectionTitle}`)
  button.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 1 0 7.07 7.07L13 20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `

  button.addEventListener('click', async (event) => {
    event.preventDefault()
    event.stopPropagation()

    const sectionUrl = buildSectionUrl(sectionId)

    try {
      await navigator.clipboard.writeText(sectionUrl)
      copyFeedback.value = `Link kopiert: ${sectionTitle}`
    } catch {
      copyFeedback.value = `Kopieren fehlgeschlagen: ${sectionTitle}`
    }

    clearCopyFeedbackSoon()
  })

  return button
}

function enhanceHeadingLinks() {
  if (!articleContentRef.value || import.meta.server) return

  const headings = articleContentRef.value.querySelectorAll<HTMLElement>('h2, h3, h4')

  headings.forEach((heading) => {
    heading.querySelector('.heading-copy-link-btn')?.remove()

    const headingAnchor = heading.querySelector<HTMLAnchorElement>('a[href^="#"]')
    if (headingAnchor) {
      headingAnchor.tabIndex = -1
      headingAnchor.classList.add('heading-text-link')
    }

    const sectionId = heading.id || headingAnchor?.getAttribute('href')?.replace(/^#/, '')
    if (!sectionId) return

    if (!heading.id) {
      heading.id = sectionId
    }

    const sectionTitle = (heading.textContent || 'Abschnitt').replace(/\s+/g, ' ').trim()
    heading.appendChild(createCopyButton(sectionId, sectionTitle))
  })
}

onMounted(async () => {
  await nextTick()
  enhanceHeadingLinks()
})

watch(() => route.path, async () => {
  await nextTick()
  enhanceHeadingLinks()
})

watch(() => article.value?.body, async () => {
  await nextTick()
  enhanceHeadingLinks()
})

useHead(() => {
  if (!article.value) return {}

  return {
    title: article.value.title,
    link: [
      { rel: 'canonical', href: canonicalUrl.value }
    ],
    meta: [
      {
        name: 'description',
        content: article.value.description
      },
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: article.value.title },
      { property: 'og:description', content: article.value.description },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:image', content: coverImageUrl.value },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: article.value.title },
      { name: 'twitter:description', content: article.value.description },
      { name: 'twitter:image', content: coverImageUrl.value },
      { property: 'article:published_time', content: article.value.date },
      ...((article.value.tags || []).map(tag => ({ property: 'article:tag', content: tag })))
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.value.title,
          description: article.value.description,
          datePublished: article.value.date,
          dateModified: article.value.date,
          image: [coverImageUrl.value],
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl.value
          },
          author: {
            '@type': 'Person',
            name: 'Enrico Reinsdorf'
          },
          publisher: {
            '@type': 'Person',
            name: 'Enrico Reinsdorf'
          }
        })
      }
    ]
  }
})
</script>

<template>
  <div v-if="article" class="min-h-screen bg-white">
    <!-- Back Button -->
    <div class="bg-gray-50 border-b border-gray-200">
      <div class="container-width px-4 md:px-8 lg:px-16 py-4">
        <NuxtLink
          to="/articles"
          class="inline-flex items-center text-dark-600 hover:text-primary-500 transition-colors"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Zurück zur Artikelübersicht
        </NuxtLink>
      </div>
    </div>

    <!-- Article Header -->
    <header class="section-padding border-b border-gray-100">
      <div class="max-w-3xl mx-auto px-4 md:px-8">
        <!-- Meta -->
        <div class="flex flex-wrap items-center gap-4 text-sm text-dark-500 mb-6">
          <span>{{ formatDate(article.date) }}</span>
          <span>·</span>
          <span>{{ readingTime(article.body) }} Min. Lesezeit</span>
        </div>

        <!-- Title -->
        <h1 class="text-4xl md:text-5xl font-bold text-dark-900 leading-tight mb-6">
          {{ article.title }}
        </h1>

        <!-- Description -->
        <p class="text-xl text-dark-600 leading-relaxed mb-8">
          {{ article.description }}
        </p>

        <!-- Tags -->
        <div class="flex flex-wrap gap-2">
          <span
            v-for="tag in article.tags"
            :key="tag"
            class="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full font-medium"
          >
            {{ tag }}
          </span>
        </div>
      </div>
    </header>

    <!-- Article Content -->
    <div class="section-padding">
      <div class="max-w-3xl mx-auto px-4 md:px-8">
        <p class="sr-only" aria-live="polite">{{ copyFeedback }}</p>
        <div ref="articleContentRef" class="prose prose-lg max-w-none">
          <ContentRenderer :value="article" />
        </div>
      </div>
    </div>

    <!-- Prev / Next Navigation -->
    <div class="border-t border-gray-200">
      <div class="max-w-3xl mx-auto px-4 md:px-8 py-12">
        <nav class="flex justify-between items-start gap-8">
          <NuxtLink
            v-if="prevArticle"
            :to="prevArticle.path"
            class="group flex items-start gap-3 text-left max-w-xs"
          >
            <svg
              class="w-5 h-5 text-dark-400 group-hover:text-primary-500 transition-colors mt-1 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <div>
              <p class="text-sm text-dark-400 mb-1">Vorheriger Artikel</p>
              <p class="font-semibold text-dark-900 group-hover:text-primary-500 transition-colors">
                {{ prevArticle.title }}
              </p>
            </div>
          </NuxtLink>
          <div v-else />

          <NuxtLink
            v-if="nextArticle"
            :to="nextArticle.path"
            class="group flex items-start gap-3 text-right max-w-xs"
          >
            <div>
              <p class="text-sm text-dark-400 mb-1">Nächster Artikel</p>
              <p class="font-semibold text-dark-900 group-hover:text-primary-500 transition-colors">
                {{ nextArticle.title }}
              </p>
            </div>
            <svg
              class="w-5 h-5 text-dark-400 group-hover:text-primary-500 transition-colors mt-1 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </NuxtLink>
          <div v-else />
        </nav>
      </div>
    </div>
  </div>
</template>
